/**
 * RightRail — collapsible 320px panel on the right of the Live Map.
 *
 * Three tabs: TRACKS · ALERTS · SENSORS.
 *
 *   - TRACKS: dense list of currently-active tracks, mono columns
 *     (callsign · classification · alt · age). Click row → open the
 *     existing TrackDetailPanel via mapStore.setSelectedTrack().
 *   - ALERTS: latest unacknowledged alerts, severity color bar on left.
 *     Click row → /reports?alert={id} so the existing alert drawer opens.
 *   - SENSORS: per-sensor health, status pill, last-seen relative time.
 *
 * Collapsed state persists in localStorage so an operator's preference
 * survives reload.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Tab } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { tracksApi, sensorsApi } from '../../services/api';
import { reportsApi, type LatestAlertRow } from '../../services/reportsApi';
import { useMapStore } from '../../store/mapStore';
import HudTag from './HudTag';

const STORAGE_KEY = 'c2.rightRail.collapsed';

// ---------- types ----------

interface LiveTrackRow {
  trackId: string;
  callsign?: string | null;
  classification?: string | null;
  altitudeMSL?: number | null;
  serialNumber?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  lastSeen?: string | null;
}

interface SensorRow {
  id: string;
  name: string;
  status: 'ONLINE' | 'OFFLINE' | 'DEGRADED' | 'MAINTENANCE';
  type?: string;
  lastHeartbeat?: string | null;
}

// ---------- helpers ----------

function ageString(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

function altFt(meters: number | null | undefined): string {
  if (meters == null) return '—';
  return `${Math.round(meters * 3.28084).toLocaleString()}`;
}

function severityTone(sev: LatestAlertRow['severity']) {
  if (sev === 'CRITICAL') return 'alert' as const;
  if (sev === 'WARNING') return 'warn' as const;
  return 'sky' as const;
}

function sensorTone(s: SensorRow['status']) {
  if (s === 'ONLINE') return 'success' as const;
  if (s === 'DEGRADED') return 'warn' as const;
  if (s === 'OFFLINE') return 'alert' as const;
  return 'neutral' as const;
}

// Tab-count rollups. React Query dedupes by queryKey, so these hooks
// share the same cache as the per-tab data fetchers below — the parent
// renders the badges and the tabs render the lists with one fetch each.
function useTabCounts() {
  const tracks = useQuery({
    queryKey: ['c2', 'tracks-live'],
    queryFn: async () => {
      const res = await tracksApi.getLiveTracks();
      return (res?.data as LiveTrackRow[] | undefined) ?? [];
    },
    refetchInterval: 5_000,
    staleTime: 4_000,
  });

  const sensors = useQuery({
    queryKey: ['c2', 'sensors-list'],
    queryFn: async () => {
      const res = await sensorsApi.list({ pageSize: 200 });
      return (res?.data as SensorRow[] | undefined) ?? [];
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const alerts = useQuery({
    queryKey: ['c2', 'alerts-recent'],
    queryFn: async () => {
      const to = new Date();
      const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
      const summary = await reportsApi.getSummary({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      return summary.latestAlerts;
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const alertSeverity = (() => {
    const list = alerts.data ?? [];
    if (list.some((a) => a.severity === 'CRITICAL')) return 'alert' as const;
    if (list.some((a) => a.severity === 'WARNING')) return 'warn' as const;
    return 'neutral' as const;
  })();

  return {
    tracks: tracks.data?.length ?? null,
    alerts: alerts.data?.length ?? null,
    alertSeverity,
    sensorsOnline: sensors.data?.filter((s) => s.status === 'ONLINE').length ?? null,
    sensorsTotal: sensors.data?.length ?? null,
  };
}

// ---------- main component ----------

export default function RightRail() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Collapsed pull-tab — single button on the right edge to expand.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="h-full w-7 bg-iron-900 border-l border-iron-600 hover:bg-iron-800 flex items-center justify-center text-iron-300 hover:text-accent transition-colors"
        title="Expand C2 panel"
      >
        <ChevronDoubleLeftIcon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <aside className="h-full w-[320px] flex-shrink-0 bg-iron-900 border-l border-iron-600 flex flex-col text-iron-100">
      <div className="flex items-center justify-between px-3 h-9 border-b border-iron-600">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-iron-300">
          C2 PANEL
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="text-iron-400 hover:text-accent p-1"
          title="Collapse"
        >
          <ChevronDoubleRightIcon className="h-4 w-4" />
        </button>
      </div>

      <RailTabs />
    </aside>
  );
}

function RailTabs() {
  const counts = useTabCounts();
  // Tab definitions colocated so the badge logic for each lives next to
  // its label. ALERTS' badge tone tracks the highest active severity so
  // operators glancing at the rail see a red badge for any CRITICAL.
  const tabs = [
    {
      label: 'TRACKS',
      count: counts.tracks,
      tone: 'neutral' as const,
    },
    {
      label: 'ALERTS',
      count: counts.alerts,
      tone: counts.alerts && counts.alerts > 0 ? counts.alertSeverity : ('neutral' as const),
    },
    {
      label: 'SENSORS',
      count:
        counts.sensorsTotal != null
          ? `${counts.sensorsOnline ?? 0}/${counts.sensorsTotal}`
          : null,
      tone:
        counts.sensorsOnline != null && counts.sensorsTotal != null
          ? counts.sensorsOnline === counts.sensorsTotal
            ? ('success' as const)
            : counts.sensorsOnline === 0
              ? ('alert' as const)
              : ('warn' as const)
          : ('neutral' as const),
    },
  ];

  return (
    <Tab.Group>
      <Tab.List className="flex border-b border-iron-600 bg-iron-800">
        {tabs.map((t) => (
          <Tab as={Fragment} key={t.label}>
            {({ selected }) => (
              <button
                type="button"
                className={clsx(
                  'flex-1 py-2 px-2 text-[11px] font-mono font-semibold uppercase tracking-[0.15em] outline-none transition-colors flex items-center justify-center gap-2',
                  selected
                    ? 'text-accent border-b-2 border-accent -mb-px bg-iron-900'
                    : 'text-iron-400 hover:text-iron-200',
                )}
              >
                <span>{t.label}</span>
                <TabCountBadge count={t.count} tone={t.tone} />
              </button>
            )}
          </Tab>
        ))}
      </Tab.List>

      <Tab.Panels className="flex-1 overflow-hidden">
        <Tab.Panel className="h-full overflow-y-auto"><TracksTab /></Tab.Panel>
        <Tab.Panel className="h-full overflow-y-auto"><AlertsTab /></Tab.Panel>
        <Tab.Panel className="h-full overflow-y-auto"><SensorsTab /></Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}

function TabCountBadge({
  count,
  tone,
}: {
  count: number | string | null;
  tone: 'neutral' | 'alert' | 'warn' | 'success';
}) {
  // null = data not loaded yet — render a small placeholder dot so the
  // tab doesn't visually shift width when the badge fills in.
  if (count == null) {
    return <span className="text-[10px] text-iron-600">–</span>;
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center min-w-[1.25rem] px-1 h-4 text-[10px] font-mono font-semibold tabular-nums rounded-sm border',
        tone === 'alert' && 'bg-alert/15 text-alert border-alert/40',
        tone === 'warn' && 'bg-warn/15 text-warn border-warn/40',
        tone === 'success' && 'bg-success/15 text-success border-success/40',
        tone === 'neutral' && 'bg-iron-700 text-iron-200 border-iron-600',
      )}
    >
      {count}
    </span>
  );
}

// ---------- tabs ----------

function TracksTab() {
  const setSelectedTrack = useMapStore((s) => s.setSelectedTrack);
  const selectedTrackId = useMapStore((s) => s.selectedTrackId);

  const query = useQuery({
    queryKey: ['c2', 'tracks-live'],
    queryFn: async () => {
      const res = await tracksApi.getLiveTracks();
      return (res?.data as LiveTrackRow[] | undefined) ?? [];
    },
    refetchInterval: 5_000,
    staleTime: 4_000,
  });

  // Sort: most-recent first.
  const sorted = useMemo(() => {
    if (!query.data) return [];
    return [...query.data].sort((a, b) => {
      const ta = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
      const tb = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
      return tb - ta;
    });
  }, [query.data]);

  if (query.isLoading) return <ListSkeleton />;
  if (sorted.length === 0) {
    return (
      <EmptyState
        title="No active tracks"
        body="Tracks appear here as soon as a sensor reports them."
      />
    );
  }

  return (
    <ul className="divide-y divide-iron-700">
      {sorted.map((t) => {
        const label =
          t.callsign ||
          t.serialNumber?.slice(-4) ||
          t.trackId.slice(-6).toUpperCase();
        const isSelected = selectedTrackId === t.trackId;
        return (
          <li key={t.trackId}>
            <button
              type="button"
              onClick={() => setSelectedTrack(t.trackId)}
              className={clsx(
                'w-full text-left px-3 py-2 hover:bg-iron-800 transition-colors',
                isSelected && 'bg-iron-800',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[12px] text-accent truncate">
                  {label}
                </span>
                <span className="font-mono text-[10px] text-iron-400 tabular-nums">
                  {ageString(t.lastSeen)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-[10px] text-iron-300 truncate">
                  {t.classification ?? 'UNKNOWN'}
                  {t.model ? ` · ${t.model}` : ''}
                </span>
                <span className="font-mono text-[10px] text-iron-300 tabular-nums">
                  {altFt(t.altitudeMSL)} <span className="text-iron-400">ft</span>
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function AlertsTab() {
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['c2', 'alerts-recent'],
    queryFn: async () => {
      const to = new Date();
      const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
      const summary = await reportsApi.getSummary({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      return summary.latestAlerts;
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  if (query.isLoading) return <ListSkeleton />;
  if (!query.data || query.data.length === 0) {
    return (
      <EmptyState
        title="No recent alerts"
        body="The last 24 hours are clear."
      />
    );
  }

  return (
    <ul className="divide-y divide-iron-700">
      {query.data.map((a) => (
        <li key={a.id}>
          <button
            type="button"
            onClick={() => navigate(`/reports?alert=${a.id}`)}
            className="w-full text-left px-3 py-2 hover:bg-iron-800 transition-colors flex items-start gap-2"
          >
            <span
              className={clsx(
                'mt-1 w-0.5 self-stretch flex-shrink-0',
                a.severity === 'CRITICAL' && 'bg-alert',
                a.severity === 'WARNING' && 'bg-warn',
                a.severity === 'INFO' && 'bg-sky',
              )}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <HudTag tone={severityTone(a.severity)}>{a.severity}</HudTag>
                <span className="font-mono text-[10px] text-iron-400">
                  {ageString(a.createdAt)}
                </span>
              </div>
              <div className="mt-1 text-[12px] text-iron-100 truncate">
                {a.title}
              </div>
              <div className="text-[10px] text-iron-400 truncate">
                {a.type}
                {a.zoneName ? ` · ${a.zoneName}` : ''}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function SensorsTab() {
  const query = useQuery({
    queryKey: ['c2', 'sensors-list'],
    queryFn: async () => {
      const res = await sensorsApi.list({ pageSize: 200 });
      return (res?.data as SensorRow[] | undefined) ?? [];
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  if (query.isLoading) return <ListSkeleton />;
  if (!query.data || query.data.length === 0) {
    return (
      <EmptyState
        title="No sensors registered"
        body="Add sensors from the Sensors page to see them here."
      />
    );
  }

  return (
    <ul className="divide-y divide-iron-700">
      {query.data.map((s) => (
        <li key={s.id} className="px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[12px] text-iron-100 truncate">
              {s.name}
            </span>
            <HudTag tone={sensorTone(s.status)}>{s.status}</HudTag>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5 text-[10px] text-iron-400">
            <span className="truncate">{s.type ?? '—'}</span>
            <span className="font-mono">last {ageString(s.lastHeartbeat)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------- shared bits ----------

function ListSkeleton() {
  return (
    <div className="px-3 py-2 space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeleton h-8 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-4 py-8 text-center">
      <div className="text-[12px] font-semibold text-iron-200">{title}</div>
      <div className="text-[11px] text-iron-400 mt-1">{body}</div>
    </div>
  );
}
